import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  ArrowLeft, Shield, Users, Award, Briefcase, 
  CheckCircle2, Clock, BookOpen, Calendar, Star,
  Loader2, CreditCard, Lock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';

const PaymentPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Basic validation
    if (!formData.firstname || !formData.email || !formData.phone) {
      setError('Please fill all required fields');
      setLoading(false);
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      setLoading(false);
      return;
    }

    // Phone validation (at least 10 digits)
    if (formData.phone.replace(/\D/g, '').length < 10) {
      setError('Please enter a valid phone number');
      setLoading(false);
      return;
    }

    try {
      // Fetch the PayU redirect HTML from backend
      const response = await fetch(`${API_URL}/api/payments/initiate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstname: formData.firstname,
          lastname: formData.lastname || '',
          email: formData.email,
          phone: formData.phone,
          course_name: 'CSDGPM - Digital Marketing Course',
          amount: 1699.00
        })
      });

      if (!response.ok) {
        throw new Error('Payment initiation failed');
      }

      // Get the HTML response which contains the auto-submit form
      const html = await response.text();
      
      // Write the HTML to the page - this will auto-submit to PayU
      document.open();
      document.write(html);
      document.close();
      
    } catch (err) {
      console.error('Payment error:', err);
      setError('Failed to initiate payment. Please try again.');
      setLoading(false);
    }
  };

  const courseDetails = {
    title: 'CSDGPM - Certified Specialist in Digital & Performance Marketing',
    duration: '6 Months',
    hours: '150 Live Hours',
    batchStart: 'New Batches Every Month',
    originalPrice: '$599 USD',
    discountedPrice: '$299 USD',
    bookingAmount: '₹1,699 INR (~$20 USD)',
    features: [
      'Exemplar Global Certification',
      '150+ Hours Live Training',
      'Real Campaign Projects',
      '100% Placement Assistance',
      'Lifetime Access to Materials',
      '1:1 Mentorship Sessions'
    ]
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Christmas Theme */}
      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1a472a 0%, #0d2818 50%, #0a1f14 100%)' }}>
        {/* Snowflakes Animation */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(30)].map((_, i) => (
            <div
              key={i}
              className="absolute text-white opacity-60"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-20px`,
                fontSize: `${Math.random() * 20 + 10}px`,
                animation: `snowfall ${Math.random() * 5 + 5}s linear infinite`,
                animationDelay: `${Math.random() * 5}s`
              }}
            >
              ❄
            </div>
          ))}
        </div>

        {/* Glowing Ornaments */}
        <div className="absolute top-20 left-20 w-4 h-4 rounded-full bg-red-500 shadow-lg shadow-red-500/50 animate-pulse"></div>
        <div className="absolute top-40 right-32 w-3 h-3 rounded-full bg-yellow-400 shadow-lg shadow-yellow-400/50 animate-pulse"></div>
        <div className="absolute bottom-40 right-20 w-4 h-4 rounded-full bg-red-500 shadow-lg shadow-red-500/50 animate-pulse"></div>

        {/* Navigation */}
        <div className="container mx-auto max-w-7xl px-6 py-4 relative z-10">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/csdgpm-offer')}
              className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
              Back to Course
            </button>
            <h1 className="text-xl font-bold text-white">🎄 AI School for All</h1>
            <div className="w-24"></div>
          </div>
        </div>

        {/* Hero Content */}
        <div className="container mx-auto max-w-7xl px-6 py-12 pb-20 relative z-10">
          <div className="text-center max-w-4xl mx-auto">
            {/* Christmas Badge */}
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full mb-6" style={{ background: 'linear-gradient(90deg, #c41e3a, #8b0000)', boxShadow: '0 0 30px rgba(196, 30, 58, 0.5)' }}>
              <span className="text-2xl">🎄</span>
              <span className="text-white text-lg font-bold">SECURE PAYMENT</span>
              <span className="text-2xl">🔒</span>
            </div>
            
            {/* Main Headline */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4 leading-tight">
              <span className="text-white drop-shadow-lg">Block Your Seat @</span>
              <span className="block mt-4 text-5xl md:text-6xl" style={{ 
                background: 'linear-gradient(90deg, #ffd700, #ffec8b, #ffd700)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 0 20px rgba(255, 215, 0, 0.5))'
              }}>
                ₹1,699 INR
              </span>
              <span className="block text-xl md:text-2xl text-white/80 mt-2">(~$20 USD)</span>
            </h1>
            
            {/* Trust Icons */}
            <div className="flex flex-wrap justify-center gap-6 text-sm mt-8">
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Lock className="w-5 h-5 text-green-400" />
                <span className="text-white font-medium">256-bit SSL</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <CreditCard className="w-5 h-5 text-yellow-400" />
                <span className="text-white font-medium">PayU Secured</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full">
                <Shield className="w-5 h-5 text-green-400" />
                <span className="text-white font-medium">PCI Compliant</span>
              </div>
            </div>
          </div>
        </div>

        {/* CSS for snowfall animation */}
        <style>{`
          @keyframes snowfall {
            0% { transform: translateY(-20px) rotate(0deg); opacity: 0; }
            10% { opacity: 0.6; }
            90% { opacity: 0.6; }
            100% { transform: translateY(100vh) rotate(360deg); opacity: 0; }
          }
        `}</style>
      </div>

      {/* Main Content */}
      <div className="container mx-auto max-w-7xl px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-10">
          
          {/* LEFT - Payment Form */}
          <div>
            <Card className="shadow-xl border-0">
              <CardHeader className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-t-xl">
                <CardTitle className="text-2xl font-bold text-center flex items-center justify-center gap-2">
                  <CreditCard className="w-6 h-6" />
                  Payment Details
                </CardTitle>
                <p className="text-center text-green-100 text-sm">Enter your details to proceed with secure payment</p>
              </CardHeader>
              <CardContent className="p-8">
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6" data-testid="error-message">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="payment-form">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstname" className="text-gray-700 font-medium">First Name *</Label>
                      <Input
                        id="firstname"
                        name="firstname"
                        value={formData.firstname}
                        onChange={handleChange}
                        placeholder="John"
                        className="mt-1"
                        required
                        data-testid="firstname-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastname" className="text-gray-700 font-medium">Last Name</Label>
                      <Input
                        id="lastname"
                        name="lastname"
                        value={formData.lastname}
                        onChange={handleChange}
                        placeholder="Doe"
                        className="mt-1"
                        data-testid="lastname-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="email" className="text-gray-700 font-medium">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      className="mt-1"
                      required
                      data-testid="email-input"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-gray-700 font-medium">Phone Number *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 9876543210"
                      className="mt-1"
                      required
                      data-testid="phone-input"
                    />
                  </div>

                  {/* Payment Summary */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-5 border border-green-200">
                    <p className="text-sm font-semibold text-gray-700 mb-3">Payment Summary</p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Course Seat Booking</span>
                        <span className="font-medium">₹1,699 <span className="text-gray-500">(~$20 USD)</span></span>
                      </div>
                      <div className="border-t border-green-200 pt-2 mt-2">
                        <div className="flex justify-between items-center">
                          <span className="font-bold text-gray-900">Total Amount</span>
                          <div className="text-right">
                            <span className="font-black text-2xl text-green-600">₹1,699</span>
                            <span className="block text-xs text-gray-500">(≈ $20 USD)</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full py-6 text-lg font-bold bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700"
                    data-testid="pay-now-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin mr-2" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Lock className="w-5 h-5 mr-2" />
                        Pay ₹1,699 (~$20 USD) Securely
                      </>
                    )}
                  </Button>

                  <div className="flex items-center justify-center gap-4 pt-2">
                    <img src="https://www.payumoney.com/images/icons/visa.png" alt="Visa" className="h-6 opacity-70" loading="lazy" decoding="async" onError={(e) => { e.target.style.display='none'; }} />
                    <img src="https://www.payumoney.com/images/icons/mastercard.png" alt="Mastercard" className="h-6 opacity-70" loading="lazy" decoding="async" onError={(e) => { e.target.style.display='none'; }} />
                    <img src="https://www.payumoney.com/images/icons/rupay.png" alt="RuPay" className="h-6 opacity-70" loading="lazy" decoding="async" onError={(e) => { e.target.style.display='none'; }} />
                    <span className="text-xs text-gray-500 font-medium">Powered by PayU</span>
                  </div>

                  <p className="text-center text-xs text-gray-500">
                    🔒 Your payment is secured by PayU's 256-bit SSL encryption
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* RIGHT - Course Details */}
          <div className="space-y-6">
            {/* Course Card */}
            <Card className="shadow-xl border-0 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-xs font-bold">BESTSELLER</span>
                  <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold">50% OFF</span>
                </div>
                <h2 className="text-xl font-bold mb-2">{courseDetails.title}</h2>
              </div>
              
              <CardContent className="p-6">
                {/* Key Info */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Clock className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-xs text-gray-500">Duration</p>
                      <p className="font-bold text-gray-900">{courseDetails.duration}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <BookOpen className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-xs text-gray-500">Training</p>
                      <p className="font-bold text-gray-900">{courseDetails.hours}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <Calendar className="w-5 h-5 text-blue-600" />
                    <div>
                      <p className="text-xs text-gray-500">Batch Starts</p>
                      <p className="font-bold text-gray-900">{courseDetails.batchStart}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <Star className="w-5 h-5 text-yellow-600" />
                    <div>
                      <p className="text-xs text-gray-500">Booking Amount</p>
                      <p className="font-bold text-green-600">{courseDetails.bookingAmount}</p>
                    </div>
                  </div>
                </div>

                {/* Price */}
                <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 mb-6 border border-green-200">
                  <p className="text-sm text-gray-600 mb-1">Full Course Fee</p>
                  <div className="flex items-center gap-3">
                    <span className="text-gray-400 line-through text-lg">{courseDetails.originalPrice}</span>
                    <span className="text-3xl font-black text-green-600">{courseDetails.discountedPrice}</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Balance payable before batches running</p>
                </div>

                {/* Features */}
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-3">What's Included:</p>
                  <div className="space-y-2">
                    {courseDetails.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Trust Indicators */}
            <div className="bg-white rounded-xl p-6 shadow-lg">
              <p className="text-sm font-semibold text-gray-700 mb-4 text-center">Why 30,000+ Students Trust Us</p>
              <div className="grid grid-cols-4 gap-4 text-center">
                <div>
                  <p className="text-2xl font-bold text-blue-600">30K+</p>
                  <p className="text-xs text-gray-500">Students</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">95%</p>
                  <p className="text-xs text-gray-500">Placement</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">500+</p>
                  <p className="text-xs text-gray-500">Partners</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">4.9★</p>
                  <p className="text-xs text-gray-500">Rating</p>
                </div>
              </div>
            </div>

            {/* Secure Payment Info */}
            <div className="bg-blue-50 rounded-xl p-6 border border-blue-100">
              <div className="flex items-center gap-3 mb-3">
                <Shield className="w-6 h-6 text-blue-600" />
                <p className="text-sm font-semibold text-gray-700">100% Secure Payment</p>
              </div>
              <p className="text-sm text-gray-600">
                Your payment is processed securely through PayU Money, India's leading payment gateway. 
                We accept all major credit cards, debit cards, net banking, and UPI.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-6 px-6 bg-gray-900 text-white text-center">
        <p className="text-sm">© 2026 AI School for All. All rights reserved.</p>
        <p className="text-xs text-gray-400 mt-2">Powered by Workshop Library | Payments by PayU</p>
      </footer>
    </div>
  );
};

export default PaymentPage;
